// Change pings and presence (RT-01; ADR-0017, program §1.2): the live surface of the API
// process. Pings say "subject X changed"; the client refetches what it shows. Message pings
// also carry the message, filtered for the subscriber (CHAT-PERF), and task pings the task card
// (WORK-PERF); both patch the cache instead.
import { z } from "zod";
import { EventSubjectKind } from "../events";
import { Timestamp, Uuid } from "../primitives";
import { MessageView } from "./chat";
import { CompanyParams } from "./common";
import { defineRoute } from "./route";
import { TaskCard } from "./work";

const base = "/v1/companies/{co}";

/** `max_seconds`: how long the stream stays open before the server ends it (client reconnects). */
export const ChangeStreamQuery = z.object({
  max_seconds: z.coerce.number().int().min(0).max(3600).optional(),
});

/**
 * The `data` of an SSE `change` event (its SSE `id` is `seq`). CHAT-PERF: a ping on a message
 * (`subject_kind` `message`: created, edited, deleted, pinned, reactions) also carries the
 * message as `messages.list` would serve it to this subscriber at delivery time, read in the
 * subscriber's own tenant transaction through the same chat read rules, so the client patches
 * its cache instead of refetching. WORK-PERF: a ping on a task (`subject_kind` `task`, every
 * type but `task.deleted`) likewise carries the task card as `tasks.list` serves it to this
 * subscriber (audience-filtered fields), read by the task list's own visibility rule. Every
 * other ping stays subject-only.
 */
export const ChangePing = z
  .object({
    subject_kind: EventSubjectKind.nullable(),
    subject_id: Uuid.nullable(),
    type: z.string(),
    seq: z.number().int(),
    message: MessageView.optional(),
    task: TaskCard.optional(),
  })
  .meta({ id: "ChangePing" });
export type ChangePing = z.infer<typeof ChangePing>;

export const PresenceSubjectRef = z
  .object({ kind: z.string(), id: Uuid })
  .meta({ id: "PresenceSubjectRef" });

export const PresencePeer = z
  .object({
    connection_id: z.string(),
    membership_id: Uuid.nullable(),
    user_id: Uuid.nullable(),
    name: z.string(),
    screen: z.string().nullable(),
    subject: PresenceSubjectRef.nullable(),
    typing: PresenceSubjectRef.nullable(),
    since: Timestamp,
  })
  .meta({ id: "PresencePeer" });
export type PresencePeer = z.infer<typeof PresencePeer>;

export const PresenceSnapshot = z
  .object({ peers: z.array(PresencePeer) })
  .meta({ id: "PresenceSnapshot" });

export const liveRoutes = {
  "changes.stream": defineRoute({
    feature: "core",
    id: "changes.stream",
    method: "get",
    path: `${base}/changes`,
    tag: "live",
    summary: "Change pings (server-sent events), filtered per principal by can()",
    description:
      "An SSE stream: `ready` on open, `change` per relayed event the caller may read " +
      "(`id` = seq, data = ChangePing), `resync` when pings may have been lost (refetch " +
      "everything shown), a `: keep-alive` comment every 15 s, and `end` (data `{reason}`: " +
      "`timeout` after max_seconds, default 1800; `revoked` when the caller lost access to the " +
      "company; `unauthenticated` when the session ended). Access is re-checked at every " +
      "batch and keep-alive. Readers get pings of the rows they may read; a seat holder " +
      "without a company role gets those of its seats.",
    access: "viewer",
    params: CompanyParams,
    query: ChangeStreamQuery,
    response: { status: 200, contentType: "text/event-stream" },
    errors: [],
    emits: [],
  }),
  "presence.get": defineRoute({
    feature: "core",
    id: "presence.get",
    method: "get",
    path: `${base}/presence`,
    tag: "live",
    summary: "Who is here now; with `Upgrade: websocket`, the presence and typing socket",
    description:
      "Without an upgrade: the peers the caller may see (managers: everyone in the company; " +
      "others: peers on the same subject). With `Upgrade: websocket` from a trusted Origin: " +
      'the client sends `{"type":"here","screen","subject"}`, `{"type":"typing","subject"}` ' +
      'and `{"type":"ping"}`; the server sends `{"type":"peers","peers":[…]}` whenever the ' +
      "visible list changes. Kept in process memory; access is re-checked every 30 s " +
      "(close 4403 when revoked, 4401 when the session ended).",
    access: "viewer",
    params: CompanyParams,
    response: { status: 200, schema: PresenceSnapshot },
    errors: ["not_ready"],
    emits: [],
  }),
} as const;
